import React from 'react';
import UserAvatar from '../../ui/UserAvatar';
import SignedStorageAttachment from '../../components/photos/SignedStorageAttachment';
import { useAttachmentViewer } from './useAttachmentViewer';
import useSessionStore from '../../state/session';
import { useToasts } from '../../ui/ToastProvider';
import { ensureQuoteChatReads } from '../../lib/quoteChatReads';
import { useKeepNewestInView } from '../../hooks/useKeepNewestInView';
import {
  fetchChatMentionStatuses,
  fetchChatMessages,
  fetchChatProfile,
  fetchChatReadReceipts,
  sendChatMessage,
  subscribeChatMentionStatuses,
  subscribeChatMessages,
  subscribeChatReadReceipts,
  uploadChatAttachment,
} from '../../data/orderChat';
import {
  fetchConversationMentionStatuses,
  fetchConversationMessages,
  fetchConversationReadReceipts,
  sendConversationMessage,
  subscribeConversationMentionStatuses,
  subscribeConversationMessages,
  subscribeConversationReadReceipts,
  uploadChatAttachment as uploadConversationChatAttachment,
  searchMentionCandidates,
  fetchChatProfile as fetchConversationChatProfile,
} from '../../data/chat';
import { getPresenceStatusColor, getPresenceStatusLabel, subscribeToUserPresence, type UserPresence } from '../../data/presence';
import './ChatPanel.css';

interface ChatPanelProps {
  conversationId?: string;
  eventId?: string;
  collapsed?: boolean;
  className?: string;
  embedded?: boolean;
  onUnreadChange?: (count: number) => void;
  headerTitle?: string;
  headerSubtitle?: string;
  placeholder?: string;
  onClose?: () => void;
  onMute?: () => void;
  onUnmute?: () => void;
  onLeave?: () => void;
  isMuted?: boolean;
  conversationType?: 'event' | 'transfer' | 'direct' | 'group';
  participantIds?: string[]; // Optional: provide participant IDs for better presence tracking
  scrubTechnicalIds?: boolean;
}

type AttachmentMeta = {
  bucket?: string | null;
  storage_path: string;
  name?: string | null;
  content_type?: string | null;
  size?: number | null;
  width?: number | null;
  height?: number | null;
};

type ProfileSummary = {
  id: string;
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  headshot_url?: string | null;
};

type MessageRow = {
  id: string;
  conversation_id?: string;
  event_id?: string;
  author_profile_id: string;
  body: string;
  attachments: AttachmentMeta[];
  mentions: Array<{ profile_id: string; display_name: string }>;
  created_at: string;
  updated_at: string;
  author?: ProfileSummary | null;
  author_display_name?: string | null;
  pinned_at?: string | null;
  pinned_by?: string | null;
  pending?: boolean;
};

type MentionStatus = {
  id: string;
  message_id: string;
  mentioned_profile_id: string;
  read_at: string | null;
  dismissed_at: string | null;
  profile?: ProfileSummary | null;
};

type MessageReadReceipt = {
  id: string;
  message_id: string;
  reader_profile_id: string;
  read_at: string;
  profile?: ProfileSummary | null;
};

type ReaderDetail = {
  profileId: string;
  displayName: string;
  readAt: string;
};

type DraftAttachment = {
  id: string;
  fileName: string;
  mimeType: string;
  size: number;
  previewUrl: string;
  uploading: boolean;
  error?: string;
  storage_path?: string;
  bucket?: string;
  width?: number;
  height?: number;
};

type MentionCandidate = {
  id: string;
  displayName: string;
};

type MentionQuery = {
  term: string;
  start: number;
  end: number;
};

const MAX_ATTACHMENTS = 6;

function resolveDisplayName(profile?: ProfileSummary | null, fallbackName?: string | null): string {
  if (profile) {
    const full = profile.full_name?.trim();
    if (full) return full;
    const combined = `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
    if (combined) return combined;
    const email = profile.email?.trim();
    if (email) return email;
  }
  const fallback = fallbackName?.trim();
  if (fallback) return fallback;
  return 'Unknown';
}

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function scrubAiTechnicalIds(value: string): string {
  return String(value || '')
    .replace(/\[((?:event_current|event_similar|event_rules|request_current):[^\]\n]+)\]/g, (_match, sourceId: string) => {
      if (sourceId.startsWith('event_current:line:')) return '(event line)';
      if (sourceId.startsWith('event_current:labor:')) return '(labor line)';
      if (sourceId.startsWith('event_current:fulfillment:')) return '(fulfillment record)';
      if (sourceId.startsWith('event_current:transfer:')) return '(transfer order)';
      if (sourceId.startsWith('event_current:catalog_accessory:')) return '(catalog accessory)';
      if (sourceId.startsWith('event_current:catalog_case:')) return '(catalog case)';
      if (sourceId.startsWith('event_similar:')) return '(similar event)';
      if (sourceId.startsWith('event_rules:')) return '(compatibility rule)';
      if (sourceId.startsWith('request_current:line:')) return '(request line)';
      if (sourceId.startsWith('request_current:analytics:')) return '(recommendation evidence)';
      if (sourceId.startsWith('request_current:transfer:')) return '(transfer history)';
      return '(evidence)';
    })
    .replace(/\[(?:event_current|event_similar|event_rules|request_current)[^\]\n]*\]/g, '(evidence)')
    .replace(/\b(?:event_current|event_similar|event_rules|request_current):[^\s,;)]+/g, 'evidence')
    .replace(/\[(?:event_current|event_similar|event_rules|request_current|event|request)(?::?[^\]\n]*)?(?=\n|$)/gi, '')
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi, 'internal record')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function ChatPanelContent({
  conversationId,
  eventId,
  collapsed = false,
  onUnreadChange,
  headerTitle = 'Chat',
  headerSubtitle = 'Collaborate with your team in real time. Screenshots welcome!',
  placeholder = 'Type a message...',
  onClose,
  onMute,
  onUnmute,
  onLeave,
  isMuted = false,
  className,
  embedded = false,
  conversationType,
  participantIds,
  scrubTechnicalIds = false,
  contextId,
}: ChatPanelProps & { contextId: string }) {
  const profileId = useSessionStore((s) => s.profileId);
  const currentProfile = useSessionStore((s) => s.profile);
  const { push } = useToasts();

  const [messages, setMessages] = React.useState<MessageRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [input, setInput] = React.useState('');
  const [draftMentions, setDraftMentions] = React.useState<MentionCandidate[]>([]);
  const [mentionQuery, setMentionQuery] = React.useState<MentionQuery | null>(null);
  const [mentionResults, setMentionResults] = React.useState<MentionCandidate[]>([]);
  const [mentionLoading, setMentionLoading] = React.useState(false);
  const [draftAttachments, setDraftAttachments] = React.useState<DraftAttachment[]>([]);
  const [sending, setSending] = React.useState(false);
  const lastReadKey = React.useMemo(
    () => `chat:lastRead:${conversationId ? `conv-${conversationId}` : `event-${eventId}`}`,
    [conversationId, eventId],
  );
  const [lastReadAt, setLastReadAt] = React.useState<string | null>(() => {
    try {
      return localStorage.getItem(lastReadKey);
    } catch {
      return null;
    }
  });
  const lastReadRef = React.useRef<string | null>(lastReadAt);

  React.useEffect(() => {
    lastReadRef.current = lastReadAt;
  }, [lastReadAt]);

  React.useEffect(() => {
    recordedReadsRef.current = new Set();
  }, [contextId, profileId]);

  React.useEffect(() => {
    try {
      const stored = localStorage.getItem(lastReadKey);
      if (stored !== null) {
        if (stored !== lastReadRef.current) {
          lastReadRef.current = stored;
          setLastReadAt(stored);
        }
      } else if (lastReadRef.current !== null) {
        lastReadRef.current = null;
        setLastReadAt(null);
      }
    } catch {
      // ignore
    }
  }, [lastReadKey]);
  const listRef = React.useRef<HTMLDivElement | null>(null);
  // When the list gets shorter (a phone keyboard opening), keep the newest message in view,
  // unless the person had scrolled up to read older ones.
  const keepListNewest = useKeepNewestInView(listRef);
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const authorCache = React.useRef(new Map<string, ProfileSummary>());
  const [mentionStatuses, setMentionStatuses] = React.useState<Record<string, MentionStatus[]>>({});
  const [readReceipts, setReadReceipts] = React.useState<Record<string, MessageReadReceipt[]>>({});
  const recordedReadsRef = React.useRef<Set<string>>(new Set());
  const [userPresences, setUserPresences] = React.useState<Record<string, UserPresence>>({});

  const meProfileSummary = React.useMemo<ProfileSummary | undefined>(() => {
    if (!profileId) return undefined;
    return {
      id: profileId,
      full_name: currentProfile?.full_name,
      first_name: currentProfile?.first_name,
      last_name: currentProfile?.last_name,
      email: currentProfile?.email,
      headshot_url: (currentProfile as any)?.headshot_url ?? null,
    };
  }, [profileId, currentProfile]);

  React.useEffect(() => {
    if (meProfileSummary) {
      authorCache.current.set(meProfileSummary.id, meProfileSummary);
    }
  }, [meProfileSummary]);

  const fetchAuthorProfile = React.useCallback(async (id: string): Promise<ProfileSummary | null> => {
    if (!id) return null;
    if (authorCache.current.has(id)) {
      return authorCache.current.get(id) ?? null;
    }
    const { data } = conversationId ? await fetchConversationChatProfile(id) : await fetchChatProfile(id);
    if (!data) return null;
    const profile = data as ProfileSummary;
    authorCache.current.set(id, profile);
    return profile;
  }, [conversationId]);

  const upsertMentionStatus = React.useCallback((incoming: MentionStatus) => {
    setMentionStatuses((prev) => {
      const list = prev[incoming.message_id] ?? [];
      const idx = list.findIndex((item) => item.id === incoming.id);
      let nextList: MentionStatus[];
      if (idx >= 0) {
        const existing = list[idx];
        nextList = list.slice();
        nextList[idx] = {
          ...existing,
          ...incoming,
          profile: incoming.profile !== undefined ? incoming.profile : existing.profile,
        };
      } else {
        nextList = [...list, incoming];
      }
      return { ...prev, [incoming.message_id]: nextList };
    });
  }, []);

  const ensureMentionProfile = React.useCallback(
    (status: MentionStatus) => {
      if (!status.mentioned_profile_id || status.profile) return;
      fetchAuthorProfile(status.mentioned_profile_id).then((profile) => {
        if (!profile) return;
        setMentionStatuses((prev) => {
          const list = prev[status.message_id];
          if (!list) return prev;
          const idx = list.findIndex((item) => item.id === status.id);
          if (idx === -1) return prev;
          const existing = list[idx];
          if (existing.profile) return prev;
          const nextList = list.slice();
          nextList[idx] = { ...existing, profile };
          return { ...prev, [status.message_id]: nextList };
        });
      });
    },
    [fetchAuthorProfile],
  );

  const upsertReadReceipt = React.useCallback((incoming: MessageReadReceipt) => {
    setReadReceipts((prev) => {
      const list = prev[incoming.message_id] ?? [];
      const idx = list.findIndex((item) => item.reader_profile_id === incoming.reader_profile_id);
      let nextList: MessageReadReceipt[];
      if (idx >= 0) {
        const existing = list[idx];
        nextList = list.slice();
        nextList[idx] = {
          ...existing,
          ...incoming,
          profile: incoming.profile !== undefined ? incoming.profile : existing.profile,
        };
      } else {
        nextList = [...list, incoming];
      }
      return { ...prev, [incoming.message_id]: nextList };
    });
  }, []);

  const ensureReceiptProfile = React.useCallback(
    (receipt: MessageReadReceipt) => {
      if (!receipt.reader_profile_id || receipt.profile) return;
      fetchAuthorProfile(receipt.reader_profile_id).then((profile) => {
        if (!profile) return;
        setReadReceipts((prev) => {
          const list = prev[receipt.message_id];
          if (!list) return prev;
          const idx = list.findIndex((item) => item.reader_profile_id === receipt.reader_profile_id);
          if (idx === -1) return prev;
          const existing = list[idx];
          if (existing.profile) return prev;
          const nextList = list.slice();
          nextList[idx] = { ...existing, profile };
          return { ...prev, [receipt.message_id]: nextList };
        });
      });
    },
    [fetchAuthorProfile],
  );

  const canSend = Boolean(profileId) && !sending;
  const unread = React.useMemo(() => {
    if (!profileId) return 0;
    if (messages.length === 0) return 0;
    const lastReadTime = lastReadAt ? Date.parse(lastReadAt) : Number.NaN;
    return messages.reduce((count, msg) => {
      if (msg.author_profile_id === profileId) return count;
      const created = Date.parse(msg.created_at);
      if (!Number.isFinite(created)) return count;
      if (!Number.isFinite(lastReadTime) || created > lastReadTime) return count + 1;
      return count;
    }, 0);
  }, [messages, profileId, lastReadAt]);

  const markAllRead = React.useCallback(() => {
    if (!messages.length) return;
    const latestMsg = messages[messages.length - 1].created_at;
    if (!latestMsg || latestMsg === lastReadRef.current) return;

    // Use current time so the read marker is always after every message.
    // Guard: never write a timestamp older than what's already stored.
    const now = new Date().toISOString();
    let newReadAt = now > latestMsg ? now : latestMsg;
    try {
      const existing = localStorage.getItem(lastReadKey);
      if (existing && existing >= newReadAt) {
        // Already marked further ahead — just update the ref so the guard
        // doesn't re-fire and return early.
        lastReadRef.current = latestMsg;
        setLastReadAt(existing);
        return;
      }
    } catch { /* ignore */ }

    lastReadRef.current = latestMsg;
    setLastReadAt(newReadAt);
    try {
      localStorage.setItem(lastReadKey, newReadAt);
    } catch {
      /* ignore */
    }
    // Notify the rest of the app so bell / home page / FAB badge update
    try {
      window.dispatchEvent(
        new CustomEvent('chat:read', { detail: { conversationId, eventId } }),
      );
    } catch {
      /* ignore */
    }
  }, [messages, lastReadKey, conversationId, eventId]);

  React.useEffect(() => {
    if (!collapsed) {
      markAllRead();
    }
  }, [collapsed, messages, markAllRead]);

  // Use ref to avoid infinite loop when onUnreadChange changes
  const onUnreadChangeRef = React.useRef(onUnreadChange);
  React.useEffect(() => {
    onUnreadChangeRef.current = onUnreadChange;
  }, [onUnreadChange]);

  React.useEffect(() => {
    if (onUnreadChangeRef.current) {
      onUnreadChangeRef.current(collapsed ? unread : 0);
    }
  }, [collapsed, unread]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const { data, error: loadErr } = conversationId
        ? await fetchConversationMessages(conversationId)
        : await fetchChatMessages(eventId!);
      if (cancelled) return;
      if (loadErr) {
        setError(loadErr.message);
        setMessages([]);
        setLoading(false);
        return;
      }
      const mappedRows = (data || []).map((row) => mapMessageRow(row));
      const missingAuthorIds = new Set<string>();
      const rowsWithAuthors = mappedRows.map((row) => {
        if (row.author) {
          authorCache.current.set(row.author_profile_id, row.author);
          return { ...row, author_display_name: resolveDisplayName(row.author, row.author_display_name) };
        }
        const cached = authorCache.current.get(row.author_profile_id);
        if (cached) {
          return { ...row, author: cached, author_display_name: resolveDisplayName(cached, row.author_display_name) };
        }
        if (row.author_profile_id && !row.author_display_name) {
          missingAuthorIds.add(row.author_profile_id);
        }
        return row;
      });
      setMessages(rowsWithAuthors);
      setLoading(false);
      if (missingAuthorIds.size > 0) {
        for (const id of missingAuthorIds) {
          if (!id) continue;
          fetchAuthorProfile(id).then((profile) => {
            if (!profile || cancelled) return;
            authorCache.current.set(id, profile);
            setMessages((prev) => {
              let updated = false;
              const next = prev.map((msg) => {
                if (msg.author_profile_id !== id || msg.author) return msg;
                updated = true;
                return {
                  ...msg,
                  author: profile,
                  author_display_name: resolveDisplayName(profile, msg.author_display_name),
                };
              });
              return updated ? next : prev;
            });
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contextId, conversationId, eventId, fetchAuthorProfile]);

  React.useEffect(() => {
    if (!conversationId && !eventId) return;
    let cancelled = false;
    const poll = async () => {
      if (document.visibilityState !== 'visible') return;
      const { data, error: pollErr } = conversationId
        ? await fetchConversationMessages(conversationId)
        : await fetchChatMessages(eventId!);
      if (cancelled) return;
      if (pollErr || !data) return;
      const mappedRows = (data || []).map((row) => mapMessageRow(row));
      setMessages((prev) => {
        const prevLatest = prev[prev.length - 1]?.id;
        const nextLatest = mappedRows[mappedRows.length - 1]?.id;
        if (prevLatest === nextLatest && prev.length === mappedRows.length) {
          return prev;
        }
        return mappedRows;
      });
    };
    const interval = window.setInterval(() => {
      void poll();
    }, 5_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [conversationId, eventId]);

  React.useEffect(() => {
    if (!conversationId && !eventId) return;
    
    const handleMessageChange = (payload: any) => {
      // Supabase real-time payload structure:
      // - eventType: 'INSERT' | 'UPDATE' | 'DELETE' (may be undefined in some cases)
      // - new: new row data (for INSERT/UPDATE)
      // - old: old row data (for UPDATE/DELETE)
      
      // Determine event type - Supabase may send it as eventType or we infer from payload structure
      const eventType = payload.eventType || (payload.new ? 'INSERT' : payload.old ? 'DELETE' : null);
      
      console.log('[chat] handleMessageChange called:', {
        eventType,
        hasNew: !!payload.new,
        hasOld: !!payload.old,
        conversationId,
        eventId,
      });
      
      // Handle INSERT events (new messages)
      if (eventType === 'INSERT' && payload.new) {
        console.log('[chat] INSERT event received, message ID:', payload.new.id, 'body:', payload.new.body?.substring(0, 50));
        let row = mapMessageRow(payload.new as any, (payload as any).new?.author);
        if (!row.author) {
          const cached = authorCache.current.get(row.author_profile_id);
          if (cached) {
            row = { ...row, author: cached, author_display_name: resolveDisplayName(cached, row.author_display_name) };
            console.log('[chat] Adding message with cached author:', row.id);
            setMessages((prev) => {
              const updated = upsertMessage(prev, row);
              console.log('[chat] Message list updated, new count:', updated.length);
              return updated;
            });
          } else if (!row.author_display_name) {
            fetchAuthorProfile(row.author_profile_id).then((profile) => {
              if (profile) {
                authorCache.current.set(row.author_profile_id, profile);
                setMessages((prev) => {
                  const updated = upsertMessage(prev, {
                    ...row,
                    author: profile,
                    author_display_name: resolveDisplayName(profile, row.author_display_name),
                  });
                  console.log('[chat] Message added with fetched profile:', row.id);
                  return updated;
                });
              } else {
                // Even without profile, add the message
                console.log('[chat] Adding message without profile:', row.id);
                setMessages((prev) => upsertMessage(prev, row));
              }
            });
            return; // Will update when profile loads
          } else {
            // Has display name but no profile object - add message anyway
            console.log('[chat] Adding message with display name only:', row.id);
            setMessages((prev) => upsertMessage(prev, row));
          }
        } else {
          authorCache.current.set(row.author_profile_id, row.author);
          row = { ...row, author_display_name: resolveDisplayName(row.author, row.author_display_name) };
          console.log('[chat] Adding message with author from payload:', row.id);
          setMessages((prev) => {
            const updated = upsertMessage(prev, row);
            console.log('[chat] Message list updated, new count:', updated.length);
            return updated;
          });
        }
      }
      // Handle UPDATE events (message edits, etc.)
      else if (eventType === 'UPDATE' && payload.new) {
        let row = mapMessageRow(payload.new as any, (payload as any).new?.author);
        if (!row.author) {
          const cached = authorCache.current.get(row.author_profile_id);
          if (cached) {
            row = { ...row, author: cached, author_display_name: resolveDisplayName(cached, row.author_display_name) };
          }
        } else {
          authorCache.current.set(row.author_profile_id, row.author);
          row = { ...row, author_display_name: resolveDisplayName(row.author, row.author_display_name) };
        }
        setMessages((prev) => upsertMessage(prev, row));
      }
      // Handle DELETE events (message deletions)
      else if (eventType === 'DELETE' && payload.old) {
        setMessages((prev) => prev.filter((m) => m.id !== payload.old.id));
      }
    };
    
    const unsubscribe = conversationId
      ? subscribeConversationMessages({
          conversationId,
          onChange: handleMessageChange,
        })
      : subscribeChatMessages({
          eventId: eventId!,
          onChange: handleMessageChange,
        });
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [conversationId, eventId, fetchAuthorProfile]);

  const participantIdsKey = React.useMemo(() => {
    if (participantIds && participantIds.length > 0) {
      return participantIds.filter((id) => id && id !== profileId).sort().join('|');
    }
    const fallbackIds = Array.from(
      new Set(messages.map((msg) => msg.author_profile_id).filter((id) => id && id !== profileId)),
    );
    return fallbackIds.sort().join('|');
  }, [participantIds, profileId, messages]);

  // Subscribe to presence for other participants
  React.useEffect(() => {
    if (!conversationId && !eventId) return;

    const participantIdsToUse = participantIdsKey ? participantIdsKey.split('|') : [];
    if (participantIdsToUse.length === 0) {
      setUserPresences({});
      return;
    }

    // Subscribe using a stable participant list so presence doesn't churn on every new message.
    const unsubscribe = subscribeToUserPresence(participantIdsToUse, setUserPresences);
    return unsubscribe;
  }, [conversationId, eventId, participantIdsKey]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = conversationId
        ? await fetchConversationMentionStatuses(conversationId)
        : await fetchChatMentionStatuses(eventId!);
      if (cancelled) return;
      if (error) {
        console.warn('[chat] mention status fetch failed', error);
        setMentionStatuses({});
        return;
      }
      const mapped = (data || []).map(mapMentionStatusRow);
      const grouped: Record<string, MentionStatus[]> = {};
      mapped.forEach((status) => {
        if (!grouped[status.message_id]) grouped[status.message_id] = [];
        grouped[status.message_id].push(status);
      });
      setMentionStatuses(grouped);
      mapped.forEach(ensureMentionProfile);
    })();
    const unsubscribe = conversationId
      ? subscribeConversationMentionStatuses({
          conversationId,
          onChange: (payload) => {
            if (!payload.new) return;
            const status = mapMentionStatusRow(payload.new as any);
            upsertMentionStatus(status);
            ensureMentionProfile(status);
          },
        })
      : subscribeChatMentionStatuses({
          eventId: eventId!,
          onChange: (payload) => {
            if (!payload.new) return;
            const status = mapMentionStatusRow(payload.new as any);
            upsertMentionStatus(status);
            ensureMentionProfile(status);
          },
        });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [ensureMentionProfile, conversationId, eventId, upsertMentionStatus]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = conversationId
        ? await fetchConversationReadReceipts(conversationId)
        : await fetchChatReadReceipts(eventId!);
      if (cancelled) return;
      if (error) {
        console.warn('[chat] read receipt fetch failed', error);
        setReadReceipts({});
        return;
      }
      const mapped = (data || []).map(mapReadReceiptRow);
      const grouped: Record<string, MessageReadReceipt[]> = {};
      mapped.forEach((receipt) => {
        if (!grouped[receipt.message_id]) grouped[receipt.message_id] = [];
        grouped[receipt.message_id].push(receipt);
      });
      setReadReceipts(grouped);
      mapped.forEach(ensureReceiptProfile);
    })();
    const unsubscribe = conversationId
      ? subscribeConversationReadReceipts({
          conversationId,
          onChange: (payload) => {
            if (!payload.new) return;
            const receipt = mapReadReceiptRow(payload.new as any);
            upsertReadReceipt(receipt);
            ensureReceiptProfile(receipt);
          },
        })
      : subscribeChatReadReceipts({
          eventId: eventId!,
          onChange: (payload) => {
            if (!payload.new) return;
            const receipt = mapReadReceiptRow(payload.new as any);
            upsertReadReceipt(receipt);
            ensureReceiptProfile(receipt);
          },
        });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [ensureReceiptProfile, conversationId, eventId, upsertReadReceipt]);

  React.useEffect(() => {
    if (!mentionQuery || !mentionQuery.term.trim()) {
      setMentionResults([]);
      setMentionLoading(false);
      return;
    }
    const term = mentionQuery.term.trim();
    let active = true;
    setMentionLoading(true);
    (async () => {
      const { data, error: searchErr } = await searchMentionCandidates(term);
      if (!active) return;
      if (searchErr) {
        setMentionResults([]);
      } else {
        const seen = new Set<string>();
        const arr = (data || [])
          .map((row: any) => ({
            id: row.id as string,
            displayName: resolveDisplayName({
              id: row.id,
              full_name: row.full_name,
              first_name: row.first_name,
              last_name: row.last_name,
              email: row.email,
            }),
          }))
          .filter((cand) => {
            if (seen.has(cand.id)) return false;
            seen.add(cand.id);
            return true;
          });
        setMentionResults(arr);
      }
      setMentionLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [mentionQuery, conversationId]);

  React.useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length]);

  React.useEffect(() => {
    return () => {
      draftAttachments.forEach((att) => URL.revokeObjectURL(att.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!profileId || collapsed) return;
    const toRecord = messages
      .filter((msg) => msg.author_profile_id !== profileId)
      .map((msg) => msg.id)
      .filter((id) => !recordedReadsRef.current.has(id));
    if (!toRecord.length) return;
    toRecord.forEach((id) => recordedReadsRef.current.add(id));
    (async () => {
      try {
        if (eventId || conversationId) {
          await ensureQuoteChatReads(
            toRecord.map((messageId) => ({
              messageId,
              eventId: eventId || undefined,
              conversationId: conversationId || undefined,
              profileId,
            })),
          );
        }
        const now = new Date().toISOString();
        const profileSummary = meProfileSummary || (profileId ? ({ id: profileId } as ProfileSummary) : undefined);
        if (!profileSummary) return;
        setReadReceipts((prev) => {
          const next = { ...prev };
          toRecord.forEach((messageId) => {
            const list = next[messageId] ?? [];
            if (list.some((item) => item.reader_profile_id === profileId)) return;
            next[messageId] = [
              ...list,
              {
                id: `local-${messageId}-${profileId}`,
                message_id: messageId,
                reader_profile_id: profileId,
                read_at: now,
                profile: profileSummary,
              },
            ];
          });
          return next;
        });
      } catch (err) {
        console.error('[chat] failed to log read receipts', err);
        toRecord.forEach((id) => recordedReadsRef.current.delete(id));
      }
    })();
  }, [collapsed, eventId, meProfileSummary, messages, profileId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInput(val);
    setDraftMentions((prev) => prev.filter((m) => val.includes(`@${m.displayName}`)));

    const cursor = e.target.selectionStart ?? val.length;
    const slice = val.slice(0, cursor);
    const match = slice.match(/@([A-Za-z0-9._\- ]{0,30})$/);
    if (match) {
      setMentionQuery({ term: match[1].trim(), start: cursor - match[0].length, end: cursor });
    } else {
      setMentionQuery(null);
    }
  };

  const attachFiles = React.useCallback(
    (fileList: FileList | File[]) => {
      const files = Array.from(fileList);
      if (files.length === 0) return;
      const remainingSlots = MAX_ATTACHMENTS - draftAttachments.length;
      if (remainingSlots <= 0) {
        push({ type: 'warn', message: `Max ${MAX_ATTACHMENTS} attachments per message.` });
        return;
      }
      const toProcess = files.slice(0, remainingSlots);
      toProcess.forEach(async (file) => {
        const id = crypto.randomUUID();
        const previewUrl = URL.createObjectURL(file);
        setDraftAttachments((prev) => [
          ...prev,
          {
            id,
            fileName: file.name,
            mimeType: file.type,
            size: file.size,
            previewUrl,
            uploading: true,
          },
        ]);
        let width: number | undefined;
        let height: number | undefined;
        if (file.type.startsWith('image/') && typeof window !== 'undefined' && 'createImageBitmap' in window) {
          try {
            const bitmap = await (window as any).createImageBitmap(file);
            width = bitmap.width;
            height = bitmap.height;
            bitmap.close();
          } catch {
            // ignore dimension errors
          }
        }
        const ext = (file.name.split('.').pop() || 'bin').toLowerCase();
        const path = `${contextId}/${Date.now()}-${id}.${ext}`;
        try {
          const uploadErr = conversationId
            ? await uploadConversationChatAttachment({ bucket: 'quote-chat', path, file })
            : await uploadChatAttachment({ bucket: 'quote-chat', path, file });
          if (uploadErr) {
            throw new Error(uploadErr.message || 'Upload failed');
          }
          setDraftAttachments((prev) =>
            prev.map((att) =>
              att.id === id
                ? {
                    ...att,
                    uploading: false,
                    storage_path: path,
                    bucket: 'quote-chat',
                    width,
                    height,
                  }
                : att,
            ),
          );
        } catch (err: any) {
          setDraftAttachments((prev) =>
            prev.map((att) => (att.id === id ? { ...att, uploading: false, error: err?.message || 'Upload failed' } : att)),
          );
          push({ type: 'error', message: `Attachment failed: ${err?.message || 'Upload failed'}` });
        }
      });
    },
    [draftAttachments.length, contextId, conversationId, push],
  );

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = e.clipboardData?.files;
    if (files && files.length) {
      const images = Array.from(files).filter((f) => f.type.startsWith('image/'));
      if (images.length) {
        e.preventDefault();
        attachFiles(images);
      }
    }
  };

  const handleMentionPick = (candidate: MentionCandidate) => {
    if (!textareaRef.current) return;
    const textarea = textareaRef.current;
    const value = input;
    const start = mentionQuery?.start ?? textarea.selectionStart ?? value.length;
    const end = mentionQuery?.end ?? textarea.selectionStart ?? value.length;
    const prefix = value.slice(0, start);
    const suffix = value.slice(end);
    const insertion = `@${candidate.displayName}`;
    const nextValue = `${prefix}${insertion} ${suffix}`;
    setInput(nextValue);
    setDraftMentions((prev) => (prev.some((m) => m.id === candidate.id) ? prev : [...prev, candidate]));
    setMentionQuery(null);
    setMentionResults([]);
    requestAnimationFrame(() => {
      textarea.focus();
      const pos = prefix.length + insertion.length + 1;
      textarea.setSelectionRange(pos, pos);
    });
  };

  const handleRemoveAttachment = (id: string) => {
    setDraftAttachments((prev) => {
      const next = prev.filter((att) => att.id !== id);
      const removed = prev.find((att) => att.id === id);
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return next;
    });
  };

  const handleSend = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!profileId || sending) return;
    const trimmed = input.trim();
    const readyAttachments = draftAttachments.filter((att) => att.storage_path && !att.uploading && !att.error);
    if (!trimmed && readyAttachments.length === 0) {
      push({ type: 'warn', message: 'Add a message or screenshot first.' });
      return;
    }
    if (draftAttachments.some((att) => att.uploading)) {
      push({ type: 'info', message: 'Please wait for uploads to finish.' });
      return;
    }
    const mentionsPayload = draftMentions.map((m) => ({ profile_id: m.id, display_name: m.displayName }));
    const attachmentsPayload = readyAttachments.map((att) => ({
      bucket: att.bucket || 'quote-chat',
      storage_path: att.storage_path!,
      name: att.fileName,
      content_type: att.mimeType,
      size: att.size,
      width: att.width,
      height: att.height,
    }));
    
    // Create optimistic message for immediate UI update
    const tempMessageId = `temp-${Date.now()}-${Math.random()}`;
    const optimisticMessage: MessageRow = {
      id: tempMessageId,
      conversation_id: conversationId || undefined,
      event_id: eventId || undefined,
      author_profile_id: profileId,
      body: trimmed,
      attachments: attachmentsPayload,
      mentions: mentionsPayload,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      author: meProfileSummary || undefined,
      author_display_name: meName,
      // Keep a local flag so the UI can show "Sending..." until we receive the real row.
      pending: true,
    };
    
    // Add optimistic message immediately
    setMessages((prev) => upsertMessage(prev, optimisticMessage));
    
    setSending(true);
    try {
      const rpcErr = conversationId
        ? await sendConversationMessage(
            {
              conversationId,
              body: trimmed,
              attachments: attachmentsPayload,
              mentions: mentionsPayload,
              authorDisplayName: meName,
              userId: profileId,
            },
          )
        : await sendChatMessage(
            {
              eventId: eventId!,
              body: trimmed,
              attachments: attachmentsPayload,
              mentions: mentionsPayload,
              authorDisplayName: meName,
              userId: profileId,
            },
          );
      if (rpcErr) throw rpcErr;

      // Confirm the optimistic message locally so "Sending..." clears even if realtime is delayed.
      setMessages((prev) =>
        prev.map((msg) => (msg.id === tempMessageId ? { ...msg, pending: false } : msg)),
      );

      // Yearly request chats may now start without a participant row.
      // After the first successful send (which joins the sender server-side),
      // refresh messages so any prior permission error clears immediately.
      if (conversationId) {
        const { data: refreshedRows, error: refreshErr } = await fetchConversationMessages(conversationId);
        if (!refreshErr) {
          setError(null);
          const mappedRows = (refreshedRows || []).map((row) => mapMessageRow(row));
          setMessages(mappedRows);
        }
      }
      
      // Clear input and drafts
      setInput('');
      setDraftMentions([]);
      setDraftAttachments((prev) => {
        prev.forEach((att) => URL.revokeObjectURL(att.previewUrl));
        return [];
      });
      setMentionQuery(null);
      setMentionResults([]);
      
      // Keep the optimistic message until the realtime insert replaces it.
    } catch (err: any) {
      // Remove optimistic message on error
      setMessages((prev) => prev.filter((m) => m.id !== tempMessageId));
      push({ type: 'error', message: err?.message || 'Failed to send message' });
    } finally {
      setSending(false);
    }
  };

  const meName = resolveDisplayName({
    id: profileId || '',
    full_name: currentProfile?.full_name,
    first_name: currentProfile?.first_name,
    last_name: currentProfile?.last_name,
    email: currentProfile?.email,
  });

  const mentionOpen = mentionQuery && (mentionLoading || mentionResults.length > 0);
  const panelSidePadding = embedded ? 'var(--space-md)' : 'var(--space-lg)';
  const panelBlockPadding = 'var(--space-md)';
  const messageListPadding = embedded ? 'var(--space-md)' : 'var(--space-lg)';
  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (event.key !== 'Enter') return;
    if (event.shiftKey) return;
    event.preventDefault();
    void handleSend();
  };

  return (
    <div
      className={className}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: embedded ? 'auto' : '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {(headerTitle || headerSubtitle || onClose || onMute || onLeave) && (
        <div
          className="pad-sm"
          style={{
            padding: `${panelBlockPadding} ${panelSidePadding}`,
            borderBottom: '1px solid var(--border)',
            background: 'var(--surface-soft)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-md)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)', flex: 1, minWidth: 0 }}>
              {headerTitle && <div style={{ fontWeight: 600 }}>{headerTitle}</div>}
              {headerSubtitle && <div className="text-xs fg-muted">{headerSubtitle}</div>}
            </div>
            {/* Action buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', flexShrink: 0 }}>
              {/* Mute/Unmute button */}
              {(onMute || onUnmute) && (
                <button
                  type="button"
                  onClick={isMuted ? onUnmute : onMute}
                  className="btn micro subtle"
                  style={{ 
                    padding: '2px 6px', 
                    fontSize: '.7rem',
                    textDecoration: isMuted ? 'line-through' : 'none',
                  }}
                  title={isMuted ? 'Unmute conversation' : 'Mute conversation'}
                >
                  🔔
                </button>
              )}
              {/* Leave button (group conversations only) */}
              {onLeave && conversationType === 'group' && (
                <button
                  type="button"
                  onClick={onLeave}
                  className="btn micro subtle"
                  style={{ padding: '2px 6px', fontSize: '.7rem' }}
                  title="Leave conversation"
                >
                  Leave
                </button>
              )}
              {/* Close button */}
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="btn micro subtle"
                  style={{ padding: '2px 6px', fontSize: '.7rem' }}
                  title="Close conversation"
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <div
        ref={keepListNewest}
        style={{
          flex: embedded ? '0 1 auto' : 1,
          minHeight: embedded ? 88 : 0,
          maxHeight: embedded ? 240 : undefined,
          overflowY: 'auto',
          padding: messageListPadding,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-md)',
        }}
      >
        {loading && <div className="text-sm fg-muted">Loading chat...</div>}
        {error && <div className="alert alert-error text-sm">{error}</div>}
        {!loading && !error && messages.length === 0 && (
          <div className="text-sm fg-muted">Be the first to start a conversation.</div>
        )}
        {messages.map((msg) => (
          <MessageBubble
            key={msg.id}
            message={msg}
            isOwnMessage={msg.author_profile_id === profileId}
            mentionStatuses={mentionStatuses[msg.id] ?? []}
            readReceipts={readReceipts[msg.id] ?? []}
            userPresences={userPresences}
            profileId={profileId}
            scrubTechnicalIds={scrubTechnicalIds}
          />
        ))}
      </div>
      <form
        onSubmit={handleSend}
        style={{
          padding: `${panelBlockPadding} ${panelSidePadding}`,
          borderTop: '1px solid var(--border)',
          position: 'relative',
        }}
      >
        <textarea
          ref={textareaRef}
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onPaste={handlePaste}
          placeholder={placeholder}
          rows={3}
          className="chat-panel__composer"
          style={{
            width: '100%',
            borderRadius: 'var(--radius-m)',
            border: '1px solid var(--border)',
            padding: 'var(--space-sm) var(--space-md)',
            resize: 'vertical',
            boxSizing: 'border-box',
            minHeight: 64,
            fontSize: '.9rem',
            marginBottom: 'var(--space-sm)',
            background: 'var(--surface)',
            color: 'var(--text)',
          }}
        />
        {draftAttachments.length > 0 && (
          <div className="flex gap-sm wrap" style={{ marginBottom: 'var(--space-sm)' }}>
            {draftAttachments.map((att) => (
              <div
                key={att.id}
                style={{
                  position: 'relative',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-s)',
                  padding: 4,
                  width: 96,
                  height: 96,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'var(--surface-soft)',
                }}
              >
                <button
                  type="button"
                  onClick={() => handleRemoveAttachment(att.id)}
                  aria-label="Remove attachment"
                  className="btn micro"
                  style={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    width: 20,
                    height: 20,
                    fontSize: 12,
                    padding: 0,
                  }}
                >
                  ×
                </button>
                {att.mimeType.startsWith('image/') ? (
                  <img src={att.previewUrl} alt={att.fileName} style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 4 }} />
                ) : (
                  <span className="text-xxs fg-muted" style={{ textAlign: 'center' }}>
                    {att.fileName}
                  </span>
                )}
                {att.uploading && (
                  <div className="text-xxs fg-muted" style={{ position: 'absolute', bottom: 4 }}>
                    Uploading...
                  </div>
                )}
                {att.error && (
                  <div className="text-xxs" style={{ position: 'absolute', bottom: 4, color: 'var(--danger-fg)' }}>
                    Failed
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', flexWrap: 'wrap', minWidth: 0, flex: '1 1 180px' }}>
            <button type="button" className="btn xs subtle" onClick={() => fileInputRef.current?.click()}>
              Attach Screenshot
            </button>
            <span className="text-xxs fg-muted" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>Signed in as {meName}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginLeft: 'auto' }}>
            <button type="submit" className="btn xs" disabled={!canSend}>
              Send
            </button>
          </div>
        </div>
        {mentionOpen && (
          <div
            style={{
              position: 'absolute',
              left: 'var(--space-lg)',
              bottom: 110,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-m)',
              boxShadow: 'var(--shadow-md)',
              width: 240,
              maxHeight: 200,
              overflow: 'auto',
              zIndex: 20,
            }}
          >
            {mentionLoading && (
              <div className="text-xxs fg-muted" style={{ padding: 'var(--space-sm)' }}>
                Searching...
              </div>
            )}
            {!mentionLoading && mentionResults.length === 0 && (
              <div className="text-xxs fg-muted" style={{ padding: 'var(--space-sm)' }}>
                No matches
              </div>
            )}
            {!mentionLoading &&
              mentionResults.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleMentionPick(option)}
                  className="btn xs subtle"
                  style={{ width: '100%', justifyContent: 'flex-start', borderRadius: 0 }}
                >
                  {option.displayName}
                </button>
              ))}
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            if (e.target.files) {
              attachFiles(e.target.files);
              e.target.value = '';
            }
          }}
        />
      </form>
    </div>
  );
}

export default function ChatPanel(props: ChatPanelProps) {
  const contextId = props.conversationId || props.eventId;
  if (!contextId) {
    return <div className="text-sm fg-muted pad-sm">No chat context provided</div>;
  }
  return <ChatPanelContent {...props} contextId={contextId} />;
}

function upsertMessage(list: MessageRow[], incoming: MessageRow): MessageRow[] {
  const idx = list.findIndex((m) => m.id === incoming.id);
  if (idx >= 0) {
    const copy = list.slice();
    const next = { ...copy[idx], ...incoming };
    if (!incoming.id.startsWith('temp-')) {
      next.pending = false;
    }
    copy[idx] = next;
    return copy.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  }
  // If adding a new message, check if there's a temp message with same content to replace it
  const tempIdx = list.findIndex(
    (m) => m.id.startsWith('temp-') && m.body === incoming.body && m.author_profile_id === incoming.author_profile_id
  );
  if (tempIdx >= 0) {
    const copy = list.slice();
    copy[tempIdx] = { ...incoming, pending: false };
    return copy.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  }
  const next = incoming.id.startsWith('temp-') ? incoming : { ...incoming, pending: false };
  return [...list, next].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
}

function mapMessageRow(row: any, authorRaw?: any): MessageRow {
  const attachments = Array.isArray(row.attachments) ? (row.attachments as AttachmentMeta[]) : [];
  const mentions = Array.isArray(row.mentions)
    ? (row.mentions as Array<{ profile_id: string; display_name: string }>)
    : [];
  const author = authorRaw ?? row.author ?? null;
  const fallbackName = typeof row.author_display_name === 'string' ? row.author_display_name : null;
  return {
    id: row.id,
    conversation_id: row.conversation_id,
    event_id: row.event_id,
    author_profile_id: row.author_profile_id,
    body: row.body || '',
    attachments,
    mentions,
    created_at: row.created_at,
    updated_at: row.updated_at,
    author,
    author_display_name: fallbackName ?? (author ? resolveDisplayName(author) : null),
    pending: false,
  };
}

function mapMentionStatusRow(row: any): MentionStatus {
  const profileRaw = row.profile ?? row.mentioned_profile ?? null;
  const profile = profileRaw
    ? {
        id: String(profileRaw.id),
        full_name: profileRaw.full_name ?? null,
        first_name: profileRaw.first_name ?? null,
        last_name: profileRaw.last_name ?? null,
        email: profileRaw.email ?? null,
      }
    : null;
  return {
    id: String(row.id ?? ''),
    message_id: String(row.message_id ?? ''),
    mentioned_profile_id: String(row.mentioned_profile_id ?? ''),
    read_at: row.read_at ?? null,
    dismissed_at: row.dismissed_at ?? null,
    profile,
  };
}

function mapReadReceiptRow(row: any): MessageReadReceipt {
  const profileRaw = row.reader ?? row.reader_profile ?? row.profile ?? null;
  const profile = profileRaw
    ? {
        id: String(profileRaw.id),
        full_name: profileRaw.full_name ?? null,
        first_name: profileRaw.first_name ?? null,
        last_name: profileRaw.last_name ?? null,
        email: profileRaw.email ?? null,
      }
    : null;
  const messageId = String(row.message_id ?? '');
  const readerId = String(row.reader_profile_id ?? '');
  const id = row.id ? String(row.id) : `receipt-${messageId}-${readerId}`;
  return {
    id,
    message_id: messageId,
    reader_profile_id: readerId,
    read_at: row.read_at ?? new Date().toISOString(),
    profile,
  };
}

function MessageBubble({
  message,
  isOwnMessage,
  mentionStatuses,
  readReceipts,
  profileId,
  userPresences,
  scrubTechnicalIds = false,
}: {
  message: MessageRow;
  isOwnMessage: boolean;
  mentionStatuses: MentionStatus[];
  readReceipts: MessageReadReceipt[];
  profileId?: string | null;
  userPresences?: Record<string, UserPresence>;
  scrubTechnicalIds?: boolean;
}) {
  const name = resolveDisplayName(message.author, message.author_display_name);
  const timestamp = new Date(message.created_at);
  const timeLabel = timestamp.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?';
  const displayBody = scrubTechnicalIds ? scrubAiTechnicalIds(message.body) : message.body;
  const mentionNodes = React.useMemo(() => renderMentions(displayBody, message.mentions), [displayBody, message.mentions]);
  // Pictures open over the chat in PhotoViewer, stepping through this message's pictures.
  const attachmentViewer = useAttachmentViewer();
  const imageAttachments = React.useMemo(() => (message.attachments || [])
    .filter((att) => att?.storage_path && String(att.content_type || '').startsWith('image/'))
    .map((att) => ({ id: `${att.bucket || 'quote-chat'}/${att.storage_path}`, title: att.name || 'Picture', storagePath: att.storage_path, bucket: att.bucket || 'quote-chat' })),
  [message.attachments]);
  const authorPresenceStatus = userPresences?.[message.author_profile_id]?.status ?? 'offline';

  const { readers, pendingNames, status, pillLabel, mentionRecipientCount } = React.useMemo(() => {
    const mentionMap = new Map<string, string>();
    const mentionRecipientIds: string[] = [];
    (message.mentions || []).forEach((mention) => {
      if (!mention?.profile_id) return;
      if (mentionMap.has(mention.profile_id)) return;
      mentionMap.set(mention.profile_id, mention.display_name);
      mentionRecipientIds.push(mention.profile_id);
    });

    const mentionReaderIds = new Set<string>();
    const readerMap = new Map<string, ReaderDetail>();

    (mentionStatuses || []).forEach((status) => {
      const profileId = status.mentioned_profile_id;
      if (!profileId) return;
      if (!status.read_at && !status.dismissed_at) return;
      const displayName = resolveDisplayName(status.profile, mentionMap.get(profileId));
      const readAt = status.read_at ?? status.dismissed_at ?? new Date().toISOString();
      mentionReaderIds.add(profileId);
      readerMap.set(profileId, { profileId, displayName, readAt });
    });

    (readReceipts || []).forEach((receipt) => {
      const profileId = receipt.reader_profile_id;
      if (!profileId || profileId === message.author_profile_id) return;
      const displayName = resolveDisplayName(receipt.profile, mentionMap.get(profileId));
      const readAt = receipt.read_at || new Date().toISOString();
      const existing = readerMap.get(profileId);
      if (!existing) {
        readerMap.set(profileId, { profileId, displayName, readAt });
        return;
      }
      const existingTime = Date.parse(existing.readAt);
      const nextTime = Date.parse(readAt);
      const preferredName = existing.displayName === 'Unknown' && displayName !== 'Unknown' ? displayName : existing.displayName;
      if (!Number.isFinite(existingTime) || !Number.isFinite(nextTime) || nextTime >= existingTime) {
        readerMap.set(profileId, {
          profileId,
          displayName: preferredName,
          readAt,
        });
      } else if (existing.displayName === 'Unknown' && displayName !== 'Unknown') {
        readerMap.set(profileId, { ...existing, displayName });
      }
    });

    const readers = Array.from(readerMap.values()).sort((a, b) => Date.parse(b.readAt) - Date.parse(a.readAt));

    const pendingNames = mentionRecipientIds
      .filter((id) => !mentionReaderIds.has(id))
      .map((id) => mentionMap.get(id) || 'Unknown');

    let status: 'read' | 'partial' | 'unread' = 'unread';
    if (mentionRecipientIds.length > 0) {
      if (mentionReaderIds.size === 0) {
        status = 'unread';
      } else if (mentionReaderIds.size === mentionRecipientIds.length) {
        status = 'read';
      } else {
        status = 'partial';
      }
    } else {
      status = readers.length > 0 ? 'read' : 'unread';
    }

    const pillLabel = (() => {
      if (mentionRecipientIds.length > 0) {
        if (status === 'read') {
          return mentionRecipientIds.length > 1 ? `Read (${mentionRecipientIds.length})` : 'Read';
        }
        if (status === 'partial') {
          return `Read ${mentionReaderIds.size}/${mentionRecipientIds.length}`;
        }
        return 'Unread';
      }
      if (status === 'read') {
        return readers.length > 1 ? `Read (${readers.length})` : 'Read';
      }
      return 'Unread';
    })();

    return { readers, pendingNames, status, pillLabel, mentionRecipientCount: mentionRecipientIds.length };
  }, [mentionStatuses, message.author_profile_id, message.mentions, readReceipts]);

  const hasMultipleReaders = readers.length > 1;
  const pillStyle = React.useMemo(() => {
    if (status === 'read') {
      return { background: 'var(--success-bg)', borderColor: 'var(--success-border)', color: 'var(--success-fg)' };
    }
    if (status === 'partial') {
      return { background: 'var(--info-bg)', borderColor: 'var(--info-border)', color: 'var(--info-fg)' };
    }
    return { background: 'var(--warn-bg)', borderColor: 'var(--warn-border)', color: 'var(--warn-fg)' };
  }, [status]);
  const pillTitle = readers.length ? `Read by ${readers.map((r) => r.displayName).join(', ')}` : 'No one has read this yet';
  const [showReaders, setShowReaders] = React.useState(false);
  React.useEffect(() => {
    if (!hasMultipleReaders) setShowReaders(false);
  }, [hasMultipleReaders]);
  const readerListId = React.useMemo(() => `message-readers-${message.id}`, [message.id]);

  const handleToggleReaders = () => {
    setShowReaders((prev) => !prev);
  };

  return (
    <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
      <UserAvatar name={name} src={message.author?.headshot_url} size={36} fallback={initials} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-sm)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
            <span style={{ fontWeight: 600 }}>{name}</span>
            {message.author_profile_id !== profileId && (
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: getPresenceStatusColor(authorPresenceStatus),
                  border: '1px solid var(--surface)',
                  flexShrink: 0,
                }}
                title={getPresenceStatusLabel(authorPresenceStatus)}
              />
            )}
          </div>
          <span className="text-xxs fg-muted">{timeLabel}</span>
          {isOwnMessage && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, position: 'relative' }}>
              {message.pending ? (
                <span className="pill micro subtle" title="Sending...">
                  Sending...
                </span>
              ) : (
                <>
                  <span className="pill micro" style={{ ...pillStyle, cursor: 'default' }} title={pillTitle}>
                    {pillLabel}
                  </span>
                  {hasMultipleReaders && (
                    <button
                      type="button"
                      className="btn micro subtle"
                      onClick={handleToggleReaders}
                      aria-expanded={showReaders}
                      aria-controls={readerListId}
                    >
                      {showReaders ? 'Hide readers' : `View readers (${readers.length})`}
                    </button>
                  )}
                  {showReaders && hasMultipleReaders && (
                    <div
                      id={readerListId}
                      className="text-xxs"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        marginTop: 6,
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-m)',
                        background: 'var(--surface)',
                        padding: 'var(--space-sm) var(--space-md)',
                        boxShadow: 'var(--shadow-md)',
                        minWidth: 180,
                        zIndex: 10,
                      }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: 4 }}>Read by</div>
                      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {readers.map((reader) => (
                          <li key={reader.profileId} style={{ display: 'flex', flexDirection: 'column' }}>
                            <span>{reader.displayName}</span>
                            <span className="fg-muted">{formatReadTimestamp(reader.readAt)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        {isOwnMessage && mentionRecipientCount > 0 && status !== 'read' && pendingNames.length > 0 && (
          <div className="text-xxs fg-muted" style={{ marginTop: 4 }}>
            Waiting on {pendingNames.join(', ')}
          </div>
        )}
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '.9rem', marginTop: 4 }}>
          {mentionNodes}
        </div>
        {message.attachments && message.attachments.length > 0 && (
          <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', marginTop: 'var(--space-sm)' }}>
            {message.attachments.map((att) => {
              if (!att?.storage_path) return null;
              const bucket = att.bucket || 'quote-chat';
              const key = `${bucket}/${att.storage_path}`;
              const isImage = String(att.content_type || '').startsWith('image/');
              return (
                <div
                  key={key}
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-s)',
                    padding: 4,
                    width: 140,
                    textAlign: 'center',
                    background: 'var(--surface-soft)',
                  }}
                >
                  <SignedStorageAttachment bucket={bucket} storagePath={att.storage_path}
                    name={att.name || 'attachment'} isImage={isImage}
                    onOpen={isImage ? () => attachmentViewer.open(imageAttachments, imageAttachments.findIndex((photo) => photo.id === key)) : undefined} />
                </div>
              );
            })}
          </div>
        )}
        {attachmentViewer.viewer}
      </div>
    </div>
  );
}

function formatReadTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function renderMentions(text: string, mentions: Array<{ profile_id: string; display_name: string }>): React.ReactNode[] {
  if (!mentions || mentions.length === 0) {
    return [text];
  }
  let segments: React.ReactNode[] = [text];
  mentions.forEach((mention, idx) => {
    const token = `@${mention.display_name}`;
    const regex = new RegExp(escapeRegExp(token), 'g');
    segments = segments.flatMap((segment) => {
      if (typeof segment !== 'string') return [segment];
      const parts = segment.split(regex);
      if (parts.length === 1) return [segment];
      const out: React.ReactNode[] = [];
      parts.forEach((part, index) => {
        if (part) out.push(part);
        if (index < parts.length - 1) {
          out.push(
            <span
              key={`${mention.profile_id}-${idx}-${index}`}
              className="pill micro"
              style={{ background: 'var(--info-bg)', border: '1px solid var(--info-border)', color: 'var(--info-fg)' }}
            >
              {token}
            </span>,
          );
        }
      });
      return out;
    });
  });
  return segments;
}
